import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, Lock, Search } from "lucide-react";
import { useState, useMemo } from "react";

interface AuditRow {
  category: string;
  categoryType: string;
  section: string;
  layoutType: string;
  allowDocuments: boolean;
  fieldName: string;
  fieldType: string;
  placeholder: string;
  instructions: string;
  isRequired: boolean;
  isCritical: boolean;
  isEncrypted: boolean;
  sortOrder: number;
}

const CATEGORY_TYPE_LABELS: Record<string, string> = {
  checklist: "Checklist",
  passwords: "Password Log",
  personal_info: "Personal Info",
  dependents: "Dependants",
  custom: "Custom",
};

function downloadCSV(rows: AuditRow[]) {
  const headers = [
    "Category", "Category Type", "Section", "Layout", "Allows Documents",
    "Field Name", "Field Type", "Placeholder",
    "Instructions / Help Text", "Required", "Critical", "Encrypted",
  ];

  const escape = (v: string | boolean | number) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const lines = [
    headers.join(","),
    ...rows.map(r => [
      r.category, r.categoryType, r.section, r.layoutType, r.allowDocuments,
      r.fieldName, r.fieldType, r.placeholder,
      r.instructions, r.isRequired, r.isCritical, r.isEncrypted,
    ].map(escape).join(",")),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `template-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TemplateAudit() {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const { data: rows = [], isLoading } = useQuery<AuditRow[]>({
    queryKey: ["/api/admin/field-config/audit"],
  });

  const categories = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach(r => seen.add(r.category));
    return ["all", ...Array.from(seen)];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      if (filterCategory !== "all" && r.category !== filterCategory) return false;
      if (!q) return true;
      return (
        r.category.toLowerCase().includes(q) ||
        r.section.toLowerCase().includes(q) ||
        r.fieldName.toLowerCase().includes(q) ||
        r.instructions.toLowerCase().includes(q) ||
        r.placeholder.toLowerCase().includes(q)
      );
    });
  }, [rows, search, filterCategory]);

  // Group for display: track when category/section changes to show divider rows
  type DisplayRow = AuditRow & { _newCategory: boolean; _newSection: boolean };
  const displayRows = useMemo<DisplayRow[]>(() => {
    let lastCat = "";
    let lastSec = "";
    return filtered.map(r => {
      const newCat = r.category !== lastCat;
      const newSec = newCat || r.section !== lastSec;
      lastCat = r.category;
      lastSec = r.section;
      return { ...r, _newCategory: newCat, _newSection: newSec };
    });
  }, [filtered]);

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading template data…</div>;
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Template Audit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only view of all template categories, sections, and fields. {rows.length} total entries.
          </p>
        </div>
        <Button onClick={() => downloadCSV(filtered)} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV ({filtered.length})
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search fields, descriptions…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8"
            autoComplete="off"
          />
        </div>
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-background"
        >
          {categories.map(c => (
            <option key={c} value={c}>{c === "all" ? "All Categories" : c}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-36">Category</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-44">Section</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Field / Item</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-28">Type</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Description / Instructions</th>
                <th className="text-center px-3 py-2 font-medium text-muted-foreground w-20">Flags</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-muted-foreground">
                    No results found.
                  </td>
                </tr>
              ) : (
                displayRows.map((row, i) => (
                  <tr
                    key={i}
                    className={[
                      "border-b last:border-0",
                      row._newCategory ? "border-t-2 border-t-border" : "",
                      row.fieldType === "default_row"
                        ? "bg-blue-50/40 dark:bg-blue-950/20"
                        : "hover:bg-muted/30",
                    ].join(" ")}
                  >
                    {/* Category — only show on first row of category */}
                    <td className="px-3 py-2 align-top">
                      {row._newCategory && (
                        <div className="space-y-0.5">
                          <div className="font-medium text-foreground">{row.category}</div>
                          <Badge variant="secondary" className="text-xs font-normal">
                            {CATEGORY_TYPE_LABELS[row.categoryType] ?? row.categoryType}
                          </Badge>
                        </div>
                      )}
                    </td>

                    {/* Section — only show on first row of section */}
                    <td className="px-3 py-2 align-top">
                      {row._newSection && (
                        <div className="space-y-0.5">
                          <div className="font-medium">{row.section}</div>
                          <span className="text-xs text-muted-foreground capitalize">{row.layoutType}</span>
                        </div>
                      )}
                    </td>

                    {/* Field name */}
                    <td className="px-3 py-2 align-top">
                      {row.fieldType === "default_row" ? (
                        <span className="text-blue-700 dark:text-blue-400 italic">
                          {row.fieldName.replace("[default row] ", "")}
                        </span>
                      ) : (
                        <span>{row.fieldName}</span>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-3 py-2 align-top">
                      {row.fieldType !== "default_row" && (
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.fieldType}</code>
                      )}
                      {row.fieldType === "default_row" && (
                        <span className="text-xs text-muted-foreground">pre-seeded</span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="px-3 py-2 align-top text-muted-foreground">
                      {row.instructions || row.placeholder || (
                        <span className="text-xs text-muted-foreground/50 italic">—</span>
                      )}
                    </td>

                    {/* Flags */}
                    <td className="px-3 py-2 align-top text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {row.isRequired && <Badge variant="outline" className="text-xs px-1">Req</Badge>}
                        {row.isCritical && <Badge variant="destructive" className="text-xs px-1">!</Badge>}
                        {row.isEncrypted && <Badge variant="secondary" className="text-xs px-1"><Lock className="h-3 w-3" aria-hidden="true" /></Badge>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
